import React, { useState, useRef, useEffect } from "react";
import { Fade } from "react-reveal";
import { greeting } from "../../portfolio.js";
import { CgSun } from "react-icons/cg/";
import { HiMoon } from "react-icons/hi";
import { style } from "glamor";
import { useLanguage } from "../../context/LanguageContext";
import translations from "../../i18n/translations";
import "./Header.css";

function Header(props) {
  const theme = props.theme;
  const activeSection = props.activeSection || "home";
  const { lang, changeLang } = useLanguage();
  const t = translations[lang];

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const NAV_LINKS = [
    { id: "home", label: t.nav.home, className: "homei" },
    { id: "experience", label: t.nav.experience, className: "xp" },
    { id: "projects", label: t.nav.projects, className: "projects" },
    { id: "education", label: t.nav.education, className: "ec" },
    { id: "contact", label: t.nav.contact, className: "cr" },
  ];

  const triggerStyles = style({
    cursor: "pointer",
    height: "36px",
    padding: "0 12px",
    marginLeft: "10px",
    borderRadius: "18px",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "rgb(252 16 86 / 76%)",
    color: "#fff",
    fontFamily: "'Google Sans Regular', sans-serif",
    fontSize: "13px",
    fontWeight: "bold",
    outline: "none",
    transition: "all 0.2s ease-in-out",
    ":hover": {
      boxShadow: `0 3px 8px ${
        props.theme.name === "light" ? "#fc10568f" : "#646464"
      }`,
    },
  });

  const themeButtonStyles = style({
    cursor: "pointer",
    height: "36px",
    width: "36px",
    borderRadius: "50%",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgb(252 16 86 / 76%)",
    outline: "none",
    transition: "all 0.2s ease-in-out",
    ":hover": {
      boxShadow: `0 3px 8px ${
        props.theme.name === "light" ? "#fc10568f" : "#646464"
      }`,
    },
  });

  const [currTheme, setCurrTheme] = useState(props.theme);

  function changeTheme() {
    if (currTheme === "light") {
      props.setTheme("dark");
      localStorage.setItem("theme", "dark");
      setCurrTheme("dark");
    } else {
      props.setTheme("light");
      localStorage.setItem("theme", "light");
      setCurrTheme("light");
    }
  }

  function scrollTo(id) {
    const menuBtn = document.getElementById("menu-btn");
    if (menuBtn) menuBtn.checked = false;
    setDropdownOpen(false);

    const el = document.getElementById(id);
    if (!el) return;
    const headerEl = document.querySelector(
      ".header-container-light, .header-container-dark"
    );
    const offset = headerEl ? headerEl.offsetHeight : 70;
    window.scrollTo({ top: el.offsetTop - offset, behavior: "smooth" });
  }

  const icon =
    props.theme.name === "dark" ? (
      <HiMoon strokeWidth={1} size={18} color="#fff" />
    ) : (
      <CgSun strokeWidth={1} size={18} color="#fff" />
    );

  const panelBg = theme.name === "dark" ? "#2a2a2a" : "#fff";
  const panelBorder = theme.name === "dark" ? "#444" : "#e0e0e0";
  const labelColor = theme.name === "dark" ? "#aaa" : "#888";

  return (
    <Fade top duration={1000} distance="20px">
      <div
        className={
          props.theme.name === "light"
            ? "header-container-light"
            : "header-container-dark"
        }
      >
        <header className="header">
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("home");
            }}
            className="logo"
          >
            <span style={{ color: theme.text }}></span>
            <span className="logo-name" style={{ color: theme.text }}>
              {greeting.logo_name}
            </span>
            <span style={{ color: theme.text }}></span>
          </a>
          <input className="menu-btn" type="checkbox" id="menu-btn" />
          <label className="menu-icon" htmlFor="menu-btn">
            <span className="navicon"></span>
          </label>
          <ul className="menu">
            {NAV_LINKS.map(({ id, label, className }) => (
              <li key={id} className={id === "home" ? "homei-li" : ""}>
                <a
                  href={`#${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(id);
                  }}
                  className={activeSection === id ? "current-link" : className}
                  style={{ color: theme.text }}
                >
                  {label}
                </a>
              </li>
            ))}

            <li className="nav-settings-li" ref={dropdownRef}>
              <button
                {...triggerStyles}
                onClick={() => setDropdownOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                {lang === "en" ? "EN" : "日本語"}
                <span className="dropdown-caret">▾</span>
              </button>

              {dropdownOpen && (
                <div
                  className="nav-settings-panel"
                  style={{
                    backgroundColor: panelBg,
                    border: `1px solid ${panelBorder}`,
                  }}
                >
                  <div className="settings-section">
                    <span
                      className="settings-label"
                      style={{ color: labelColor }}
                    >
                      {t.settings.language}
                    </span>
                    <div className="lang-buttons">
                      <button
                        className={`lang-btn ${lang === "en" ? "lang-btn--active" : ""}`}
                        onClick={() => {
                          changeLang("en");
                          setDropdownOpen(false);
                        }}
                      >
                        EN
                      </button>
                      <button
                        className={`lang-btn ${lang === "ja" ? "lang-btn--active" : ""}`}
                        onClick={() => {
                          changeLang("ja");
                          setDropdownOpen(false);
                        }}
                      >
                        日本語
                      </button>
                    </div>
                  </div>

                  <div className="settings-divider" style={{ borderColor: panelBorder }} />

                  <div className="settings-section settings-section--row">
                    <span
                      className="settings-label"
                      style={{ color: labelColor }}
                    >
                      {t.settings.theme}
                    </span>
                    <button {...themeButtonStyles} onClick={changeTheme}>
                      {icon}
                    </button>
                  </div>
                </div>
              )}
            </li>
          </ul>
        </header>
      </div>
    </Fade>
  );
}

export default Header;
